import React, { FC, useMemo, useState } from 'react'

import {
  alpha,
  Box,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { StyledSwitch } from '../../../components/CustomSwitch'
import { useAppSelector } from '../../../store/hooks'
import { fCurrencyShort } from '../../../utils/formatNumber'
import { currencyFormatter } from '../../../utils/helpFunctions'
import { EmptyData } from '../EmptyData'
import { ReportWidgetsHeader } from '../ReportWidgetsHeader'
import { StyledWidgetWrapper } from '../StyledWidgetWrapper'

type PerformanceRow = {
  title: string
  caption: string
  trades: number
  rate: number
  rateLabel: string
  avgPnl: number
  totalPnl?: number
  extraLabel?: string
  extraValue?: string
  isNegative?: boolean
  emotionGroup?: 'risk' | 'context' | 'regulating'
}

type PsychologyPerformanceWidgetProps = {
  title: string
  subtitle?: string | null
  explanation?: string
  rows: PerformanceRow[]
  maxRowsHeight?: number | string
  emotionFilter?: boolean
  showSummary?: boolean
  headlineMinTrades?: number
  fillHeight?: boolean
}

const clampPercent = (value: number) => Math.max(0, Math.min(100, Number(value) || 0))
export const PsychologyPerformanceWidget: FC<PsychologyPerformanceWidgetProps> = ({
  title,
  subtitle,
  explanation,
  rows,
  maxRowsHeight = 420,
  emotionFilter = false,
  showSummary = true,
  headlineMinTrades = 3,
  fillHeight = false,
}) => {
  const { t } = useTranslation()
  const { palette } = useTheme()
  const { currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)
  const currency = currentAccount?.currency
  const [showPositive, setShowPositive] = useState(true)
  const visibleRows = useMemo(() => rows.filter(({ trades }) => trades > 0), [rows])
  const filteredRows = useMemo(() => {
    if (!emotionFilter) return visibleRows

    return visibleRows
      .filter(({ isNegative }) => isNegative === !showPositive)
      .sort((a, b) => (showPositive ? b.avgPnl - a.avgPnl : a.avgPnl - b.avgPnl))
  }, [emotionFilter, showPositive, visibleRows])

  const summary = useMemo(() => {
    const headlineRows = filteredRows.filter(({ trades }) => trades >= headlineMinTrades)

    if (!filteredRows.length) {
      return null
    }

    const byAvgPnlDesc = [...headlineRows].sort((a, b) => b.avgPnl - a.avgPnl)
    const byTradesDesc = [...filteredRows].sort((a, b) => b.trades - a.trades)

    return {
      strongest: byAvgPnlDesc[0] || null,
      weakest: byAvgPnlDesc[byAvgPnlDesc.length - 1] || null,
      largestSample: byTradesDesc[0],
    }
  }, [filteredRows, headlineMinTrades])

  const renderMoney = (value: number) => {
    if (isNaN(value)) return '-'

    return (
      <Tooltip title={currencyFormatter(currency, value, {}, true)} arrow placement="top">
        <Typography
          variant="h6"
          sx={{
            color:
              value > 0 ? palette.green[500] : value < 0 ? palette.red[400] : palette.text.primary,
          }}
        >
          {fCurrencyShort(value, currency)}
        </Typography>
      </Tooltip>
    )
  }

  return (
    <StyledWidgetWrapper sx={{ height: fillHeight ? '100%' : 'fit-content !important' }}>
      <Stack spacing={2.5} height={fillHeight ? '100%' : 'auto'} width="100%">
        <ReportWidgetsHeader text={title} explanation={explanation}>
          {emotionFilter && (
            <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap={{ xs: 1, lg: 3 }}>
              <Typography fontWeight={500} color={!showPositive ? palette.grey[700] : 'unset'}>
                {t('positive', 'Positive')}
              </Typography>
              <StyledSwitch
                checked={!showPositive}
                onChange={() => setShowPositive((previous) => !previous)}
              />
              <Typography fontWeight={500} color={showPositive ? palette.grey[700] : 'unset'}>
                {t('negative', 'Negative')}
              </Typography>
            </Box>
          )}
        </ReportWidgetsHeader>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}

        {!filteredRows.length ? (
          <Box mt={4}>
            <EmptyData
              title={t('notEnoughData', 'Not enough data')}
              subtitle={t(
                'pleaseCompleteJournalingSection',
                'Please complete "Journaling" section',
              )}
            />
          </Box>
        ) : (
          <Stack spacing={2.5} minWidth={0}>
            {showSummary && summary && (
              <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }}
                gap={2}
              >
                {[
                  {
                    label: t('psychologyPage.widgets.shared.strongestSignal', 'Strongest signal'),
                    row: summary.strongest,
                  },
                  {
                    label: t('psychologyPage.widgets.shared.weakestSignal', 'Weakest signal'),
                    row: summary.weakest,
                  },
                  {
                    label: t('psychologyPage.widgets.shared.largestSample', 'Largest sample'),
                    row: summary.largestSample,
                  },
                ].map((item) => (
                  <Stack
                    key={`${item.label}-${item.row?.title || 'insufficient-sample'}`}
                    spacing={0.5}
                    p={1.5}
                    border={`1px solid ${palette.divider}`}
                    borderRadius={2}
                    minWidth={0}
                    bgcolor={
                      palette.mode === 'dark'
                        ? palette.background.default
                        : alpha(palette.common.white, 0.55)
                    }
                  >
                    <Typography variant="overline" color="text.secondary" noWrap>
                      {item.label}
                    </Typography>
                    <Typography variant="subtitle2" noWrap title={item.row?.title}>
                      {item.row?.title || '—'}
                    </Typography>
                    {item.label ===
                    t('psychologyPage.widgets.shared.largestSample', 'Largest sample') ? (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {item.row.caption}
                      </Typography>
                    ) : !item.row ? (
                      <Typography variant="body2" color="text.secondary">
                        {t('psychologyPage.widgets.shared.insufficientSample', 'Need 3+ trades')}
                      </Typography>
                    ) : (
                      renderMoney(item.row.avgPnl)
                    )}
                  </Stack>
                ))}
              </Box>
            )}

            <Stack
              divider={<Divider flexItem />}
              spacing={0.5}
              minWidth={0}
              maxHeight={maxRowsHeight}
              overflow="auto"
              pr={0.5}
            >
              {filteredRows.map((row) => {
                const progressColor =
                  row.avgPnl > 0
                    ? palette.green[400]
                    : row.avgPnl < 0
                    ? palette.red[400]
                    : palette.blue[400]

                return (
                  <Stack
                    key={`${row.title}-${row.caption}`}
                    direction={{ xs: 'column', lg: 'row' }}
                    justifyContent="space-between"
                    gap={2}
                    py={1.25}
                    minWidth={0}
                  >
                    <Stack spacing={0.5} flex={1} minWidth={0}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        minWidth={0}
                      >
                        <Typography variant="subtitle2" noWrap title={row.title}>
                          {row.title}
                        </Typography>
                        {row.emotionGroup === 'context' && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="warning"
                            label={t(
                              'psychologyPage.widgets.emotionPerformance.contextDependent',
                              'Context-dependent',
                            )}
                          />
                        )}
                        <Chip
                          size="small"
                          label={`${row.rateLabel}: ${Math.round(row.rate)}%`}
                          sx={{
                            bgcolor: alpha(progressColor as string, 0.12),
                            color: progressColor,
                            fontWeight: 600,
                          }}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {row.caption}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={clampPercent(row.rate)}
                        sx={{
                          maxWidth: { xs: '100%', lg: 280 },
                          height: 8,
                          borderRadius: 999,
                          bgcolor: alpha(progressColor as string, 0.15),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 999,
                            backgroundColor: progressColor,
                          },
                        }}
                      />
                    </Stack>

                    <Stack
                      spacing={0.35}
                      alignItems={{ xs: 'flex-start', lg: 'flex-end' }}
                      minWidth={{ xs: 0, lg: 170 }}
                    >
                      {renderMoney(row.avgPnl)}
                      <Typography variant="caption" color="text.secondary">
                        {t('psychologyPage.widgets.shared.avgPnl', '{{value}} avg P&L', {
                          value: '',
                        })
                          .replace('{{value}} ', '')
                          .trim()}
                      </Typography>
                      {typeof row.totalPnl === 'number' && (
                        <Typography variant="caption" color="text.secondary">
                          {t('psychologyPage.widgets.shared.totalPnl', 'Total')}:&nbsp;
                          {currencyFormatter(currency, row.totalPnl, {}, true)}
                        </Typography>
                      )}
                      {row.extraLabel && row.extraValue && (
                        <Typography variant="caption" color="text.secondary">
                          {row.extraLabel}: {row.extraValue}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                )
              })}
            </Stack>
          </Stack>
        )}
      </Stack>
    </StyledWidgetWrapper>
  )
}
